import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ShieldCheck, Landmark, Coins, ExternalLink, RefreshCw, FileText } from 'lucide-react';
import { OfficialToken } from '../../components/pas/OfficialToken';

const B = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const Stat = ({ label, value, sub, testid, accent = 'text-white' }) => (
  <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6" data-testid={testid}>
    <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`text-3xl font-bold mt-2 font-mono ${accent}`}>{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

const AssetRow = ({ a }) => (
  <tr className="border-t border-slate-800" data-testid={`reserve-asset-${a.id}`}>
    <td className="py-3 pr-4"><p className="text-white text-sm">{a.description}</p><p className="text-[11px] text-slate-500 font-mono">Custodian wallet {a.custodian.slice(0, 8)}…{a.custodian.slice(-4)}</p></td>
    <td className="py-3 pr-4 font-mono text-sm text-slate-200">{money(a.face_usd)}</td>
    <td className="py-3 pr-4 font-mono text-sm text-emerald-300">{money(a.market_usd)}</td>
    <td className="py-3 pr-4 text-sm text-slate-300">{a.maturity || '—'}</td>
    <td className="py-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded ${a.status === 'held' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>{a.status}</span></td>
  </tr>
);

export default function PublicReserves() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const load = () => { setErr(''); axios.get(`${B}/api/public/reserves`).then(r => setD(r.data)).catch(() => setErr('Could not read the blockchain right now. Try again shortly.')); };
  useEffect(load, []);
  const pct = d?.coverage_pct;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="public-reserves-page">
      <header className="max-w-6xl mx-auto px-6 pt-10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white font-bold" data-testid="reserves-home-link">
          <img src={`${B}/api/token-list/beth-usd.png`} alt="BETH USD" className="w-8 h-8 rounded-full" /> BETH USD
        </Link>
        <button onClick={load} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="reserves-refresh"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14 space-y-12">
        <section className="space-y-4 max-w-3xl">
          <p className="text-emerald-400 text-xs uppercase tracking-[0.3em]">Proof of reserves</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">Every BETH USD, backed and checkable.</h1>
          <p className="text-base text-slate-400">Live figures read straight from the BETH USD contract on Base. Nothing on this page is typed in by hand.</p>
          {d && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${d.fully_backed ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-red-500/15 text-red-300 border border-red-500/30'}`} data-testid="reserves-backed-badge">
              <ShieldCheck className="w-4 h-4" /> {d.fully_backed ? `Fully backed · ${pct?.toLocaleString()}% coverage` : 'Under-backed'}
            </div>
          )}
        </section>

        {err && <p className="text-red-400 text-sm" data-testid="reserves-error">{err}</p>}
        <div className="max-w-xl"><OfficialToken /></div>
        {!d && !err && <p className="text-slate-500 text-sm">Reading the blockchain…</p>}

        {d && (
          <>
            <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="BETH USD in circulation" value={money(d.supply_usd)} sub="Total supply on Base" testid="reserves-supply" />
              <Stat label="Total reserves" value={money(d.reserves.total_usd)} sub="Cash + Treasury bills" accent="text-emerald-300" testid="reserves-total" />
              <Stat label="Treasury bills" value={money(d.reserves.treasury_usd)} sub="Market value" testid="reserves-treasury" />
              <Stat label="Cash reserves" value={money(d.reserves.cash_usd)} sub={d.cash_attestation ? `Confirmed by reserve manager ${d.cash_attestation.created_at.slice(0, 10)}` : 'Recorded cash & USDC'} testid="reserves-cash" />
            </section>

            <section className="space-y-4">
              <h2 className="text-base md:text-lg text-white font-semibold flex items-center gap-2"><Landmark className="w-5 h-5 text-emerald-400" /> U.S. Treasury holdings</h2>
              <div className="overflow-x-auto bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
                <table className="w-full text-left" data-testid="reserves-assets-table">
                  <thead><tr className="text-[11px] uppercase text-slate-500"><th className="pb-2">Security</th><th className="pb-2">Face value</th><th className="pb-2">Market value</th><th className="pb-2">Maturity</th><th className="pb-2">Status</th></tr></thead>
                  <tbody>{d.assets.map(a => <AssetRow key={a.id} a={a} />)}</tbody>
                </table>
                {!d.assets.length && <p className="text-sm text-slate-500">No Treasury assets recorded.</p>}
              </div>
            </section>

            <section className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-2" data-testid="reserves-contract">
                <p className="text-white font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-cyan-400" /> Verify it yourself</p>
                <p className="text-xs text-slate-400">Token contract (Base, chain {d.token.chain_id})</p>
                <p className="font-mono text-xs text-slate-200 break-all">{d.token.address}</p>
                <a href={d.token.explorer_url} target="_blank" rel="noreferrer" className="text-cyan-300 text-xs inline-flex items-center gap-1 underline" data-testid="reserves-basescan-link">Open on BaseScan <ExternalLink className="w-3 h-3" /></a>
                <a id="buy" href={`https://app.uniswap.org/swap?chain=base&inputCurrency=0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913&outputCurrency=${d.token.address}`} target="_blank" rel="noreferrer" className="ml-4 inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full text-xs font-semibold" data-testid="reserves-buy-uniswap">Buy BETH USD on Uniswap <ExternalLink className="w-3 h-3" /></a>
              </div>
              {d.pool?.exists && (
                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-2" data-testid="reserves-pool">
                  <p className="text-white font-semibold flex items-center gap-2"><Coins className="w-4 h-4 text-amber-400" /> Uniswap market</p>
                  <p className="text-sm text-slate-300">Price <b className="text-white">${Number(d.pool.price_usdc_per_beth).toFixed(4)}</b> · {num(d.pool.beth_in_pool)} BETH · {num(d.pool.usdc_in_pool)} USDC available</p>
                  <a href={d.pool.explorer_url} target="_blank" rel="noreferrer" className="text-cyan-300 text-xs inline-flex items-center gap-1 underline" data-testid="reserves-pool-link">View pool <ExternalLink className="w-3 h-3" /></a>
                </div>
              )}
            </section>

            <p className="text-[11px] text-slate-500 max-w-3xl" data-testid="reserves-disclaimer">{d.disclaimer} Last checked {new Date(d.checked_at).toLocaleString()}. Not FDIC insured.</p>
          </>
        )}
      </main>
    </div>
  );
}
